/**
 * PostHog Analytics Helper
 * Gère l'identification et le tracking des utilisateurs
 */

import posthog from 'posthog-js'
import type { User } from '@supabase/supabase-js'

interface Profile {
  id: string
  display_name: string | null
  plan: 'free' | 'pro' | null
  language?: string | null
  stripe_customer_id?: string | null
  [key: string]: any
}

/**
 * Identifie l'utilisateur dans PostHog
 * À appeler après un login ou signup réussi
 */
export function identifyUser(user: User, profile: Profile | null) {
  if (!user || !posthog.__loaded) return

  // Identifie l'utilisateur avec son ID Supabase
  posthog.identify(user.id, {
    email: user.email,
    display_name: profile?.display_name || null,
    plan: profile?.plan || 'free',
    is_pro: profile?.plan === 'pro',
    language: profile?.language || 'en',
    signup_date: user.created_at,
    // Propriétés $set_once - ne changent jamais
    $set_once: {
      signup_date: user.created_at,
      initial_email: user.email,
    },
  })
}

/**
 * Met à jour les propriétés utilisateur
 * À appeler quand le plan change ou d'autres propriétés importantes
 */
export function updateUserProperties(properties: Record<string, any>) {
  if (!posthog.__loaded) return

  posthog.people.set(properties)
}

/**
 * Reset l'identification (logout)
 */
export function resetPostHog() {
  if (!posthog.__loaded) return
  posthog.reset()
}

/**
 * Capture un événement avec les propriétés standard
 */
export function captureEvent(eventName: string, properties?: Record<string, any>) {
  if (!posthog.__loaded) return
  posthog.capture(eventName, properties)
}

/**
 * Capture un événement d'erreur
 */
export function captureError(error: Error, context?: string) {
  if (!posthog.__loaded) return
  posthog.capture('error_occurred', {
    error_message: error.message,
    error_name: error.name,
    context: context || 'unknown',
  })
}
